<template>
  <div class="pdf2word-page">
    <header class="page-header">
      <div class="header-left">
        <el-icon :size="20"><Document /></el-icon>
        <span>PDF 转 Word</span>
      </div>
      <router-link to="/" class="back-link">
        <el-icon><ArrowLeft /></el-icon>
        <span>返回首页</span>
      </router-link>
    </header>

    <div class="page-body">
      <div class="convert-card">
        <div class="card-head">
          <div class="card-icon">
            <el-icon :size="36"><Document /></el-icon>
          </div>
          <h1>PDF 转 Word 工具</h1>
          <p>上传 PDF 文件，一键转换为可编辑的 Word 文档</p>
        </div>

        <el-upload
          class="uploader"
          drag
          accept=".pdf"
          :auto-upload="false"
          :limit="1"
          :file-list="fileList"
          :on-change="onFileChange"
          :on-remove="onFileRemove"
        >
          <el-icon class="upload-icon"><UploadFilled /></el-icon>
          <div class="el-upload__text">将 PDF 文件拖到此处，或 <em>点击选择文件</em></div>
          <template #tip>
            <div class="el-upload__tip">仅支持 PDF 格式，单次上传一个文件</div>
          </template>
        </el-upload>

        <div class="convert-actions">
          <el-button
            type="primary"
            size="large"
            :disabled="!selectedFile"
            :loading="converting"
            @click="uploadFile"
          >
            <el-icon><Download /></el-icon>
            <span>{{ converting ? '转换中…' : '开始转换' }}</span>
          </el-button>
        </div>

        <el-alert
          v-if="uploadStatus"
          class="status-alert"
          :title="uploadStatus"
          :type="isSuccess ? 'success' : 'error'"
          :closable="false"
          show-icon
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'Pdf2Word',
  data() {
    return {
      selectedFile: null,
      fileList: [],
      uploadStatus: '',
      isSuccess: false,
      converting: false,
    };
  },
  methods: {
    onFileChange(uploadFile) {
      this.selectedFile = uploadFile.raw;
      this.uploadStatus = '';
      this.isSuccess = false;
    },
    onFileRemove() {
      this.selectedFile = null;
      this.uploadStatus = '';
      this.isSuccess = false;
    },
    async uploadFile() {
      if (!this.selectedFile) return;

      const formData = new FormData();
      formData.append('file', this.selectedFile);
      this.converting = true;
      this.uploadStatus = '';

      try {
        const response = await axios.post('/api/pdf/pdf2word', formData, {
          responseType: 'blob',
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.status === 200) {
          this.uploadStatus = '转换成功，文件已开始下载！';
          this.isSuccess = true;

          const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
          const url = window.URL.createObjectURL(blob);
          const disposition = response.headers['content-disposition'] || '';
          const match = disposition.match(/filename\*=UTF-8''([^;]+)/i) || disposition.match(/filename="?([^";]+)"?/i);
          const fileName = match ? decodeURIComponent(match[1]) : 'converted.docx';

          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          this.uploadStatus = '文件上传失败，请重试。';
          this.isSuccess = false;
        }
      } catch (error) {
        this.uploadStatus = '文件上传失败，请重试。';
        this.isSuccess = false;
      } finally {
        this.converting = false;
      }
    }
  },
};
</script>

<style scoped>
.pdf2word-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(160deg, #eef2ff 0%, #f5f7fb 45%, #f0f4ff 100%);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC",
    "Hiragino Sans GB", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif;
  --el-color-primary: #4f6ef7;
  --el-color-primary-light-3: #7b93fa;
  --el-color-primary-light-5: #a5b6fb;
  --el-color-primary-light-7: #cdd8fd;
  --el-color-primary-light-8: #e2e9fe;
  --el-color-primary-light-9: #f0f4ff;
  --el-color-primary-dark-2: #3f58c6;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 0 24px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(232, 236, 243, 0.8);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #1f2329;
}

.header-left .el-icon {
  color: var(--el-color-primary);
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: #4b5563;
  text-decoration: none;
  transition: color 0.2s;
}

.back-link:hover {
  color: var(--el-color-primary);
}

.page-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.convert-card {
  width: 100%;
  max-width: 620px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(16, 24, 40, 0.08);
  padding: 40px;
  box-sizing: border-box;
}

.card-head {
  text-align: center;
  margin-bottom: 28px;
}

.card-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
}

.card-head h1 {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 600;
  color: #1f2329;
}

.card-head p {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
}

.uploader :deep(.el-upload) {
  width: 100%;
}

.uploader :deep(.el-upload-dragger) {
  width: 100%;
  padding: 36px 20px;
  border-radius: 12px;
  border: 2px dashed #c7d2fe;
  background: #fafbff;
  transition: all 0.2s;
}

.uploader :deep(.el-upload-dragger:hover) {
  border-color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
}

.upload-icon {
  font-size: 48px;
  color: var(--el-color-primary);
  margin-bottom: 12px;
}

.uploader :deep(.el-upload__text) {
  color: #4b5563;
  font-size: 14px;
}

.uploader :deep(.el-upload__text em) {
  color: var(--el-color-primary);
  font-style: normal;
  font-weight: 500;
}

.uploader :deep(.el-upload__tip) {
  text-align: center;
  margin-top: 8px;
  color: #9ca3af;
}

.convert-actions {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.status-alert {
  margin-top: 20px;
}
</style>
